// src/pages/product/PartnerProducts.tsx
//
// PartnerProducts (Partner-only products view)
// - Uses the shared SearchPanel via PartnerProductsSearchPanel.
// - Keeps URL query params in sync with the *applied* search state (shareable deep links).
// - Uses "draft vs applied" search state to avoid filtering while typing.
// - Auto-search is opt-in per page (do NOT implement debounce here; delegate to SearchPanel).
//
// Table policy (shared UX):
// - Replace legacy list controls with StandardTableToolbar + StandardPagination.
// - Use StandardTableFrame for loading/empty states.
// - Navigation rule: do NOT navigate on row click.
//   Navigate ONLY when clicking the Index(#) cell (index-only navigation).

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Chip, LinearProgress, TableCell, TableRow } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { utcToZonedTime } from 'date-fns-tz';
import { useRecoilValue } from 'recoil';

import { exportPartnerProductsExcel, type GetPartnerProductsParams, ProductSaleType } from '@apis/product';

import { usePartnerProductList } from '@queries/products/usePartnerProductList';
import { usePartnerProductCount } from '@queries/products/usePartnerProductCount';
import { useCountryList } from '@queries/countries/useCountryList';
import { usePartnerDetail } from '@queries/partners/usePartnerDetail';

import { userState } from '@recoils/atoms';

import { filterSearchParams } from '@utils/search';
import { KOREAN_TIME_ZONE } from '@constants/timezone';
import { downloadBlob } from '@utils/file';
import {
  PRODUCT_SALE_TYPE_LABELS,
  getProductSaleTypeMeta,
} from '@utils/productLabels';

import { usePageHeader } from '@components/common/pageHeader.context';
import type { SetSearchValueFn } from '@components/common/search';
import PartnerProductsSearchPanel from '@components/product/PartnerProductsSearchPanel';

import { StandardTableToolbar, StandardTableFrame, StandardPagination, cellSx } from '@components/common/table';

type ProductSearchForm = GetPartnerProductsParams;

type Option<T = any> = {
  label: string;
  value: T;
};

const PAGE_SIZE = 50;

/**
 * Page-level auto-search policy:
 * - Global default autoSearch is OFF (SearchPanel constants).
 * - Enable autoSearch only for this page if desired.
 */
const PAGE_AUTO_SEARCH = { enabled: false, delayMs: 500 } as const;

const clamp = (n: number, min: number, max: number) => Math.min(max, Math.max(min, n));

const sortByLabel = <T,>(a: Option<T>, b: Option<T>) =>
  a.label.localeCompare(b.label, undefined, { sensitivity: 'base' });

const normalizeText = (v: unknown) => (typeof v === 'string' ? v.trim() : '');

const parsePositiveInt = (value: string | null, fallback = 1) => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : fallback;
};

const isValidProductSaleType = (v: string | null): v is ProductSaleType => {
  if (!v) return false;
  return (Object.values(ProductSaleType) as string[]).includes(v);
};

/**
 * Normalize search form before calling APIs / building query string.
 * - Trim string values
 * - Drop invalid/empty values
 */
const cleanSearchForm = (form: ProductSearchForm): ProductSearchForm => {
  const code = normalizeText(form.productCode);
  const name = normalizeText(form.productName);

  return {
    productCode: code,
    productName: name,
    countryId: typeof form.countryId === 'number' ? form.countryId : undefined,
    productSaleType: form.productSaleType || undefined,
  };
};

const readSearchFromUrl = (sp: URLSearchParams): ProductSearchForm => {
  const saleTypeRaw = sp.get('productSaleType');

  const countryIdRaw = sp.get('countryId');
  const countryId = countryIdRaw ? Number(countryIdRaw) : undefined;

  return {
    productCode: sp.get('productCode') || '',
    productName: sp.get('productName') || '',
    countryId: Number.isFinite(countryId as number) ? countryId : undefined,
    productSaleType: isValidProductSaleType(saleTypeRaw) ? saleTypeRaw : undefined,
  };
};

const buildUrlParams = (form: ProductSearchForm, page: number) => {
  const cleaned = cleanSearchForm(form);

  const params: Record<string, string> = {};
  if (cleaned.productCode) params.productCode = cleaned.productCode;
  if (cleaned.productName) params.productName = cleaned.productName;
  if (typeof cleaned.countryId === 'number') params.countryId = String(cleaned.countryId);
  if (cleaned.productSaleType) params.productSaleType = String(cleaned.productSaleType);

  return {
    ...filterSearchParams(params),
    page: String(page),
  };
};

export const PartnerProducts = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const user = useRecoilValue(userState);
  const partnerId = Number(user?.partnerId) || 0;

  /* ------------------------------------------------------------------ */
  /* Page header (partner name)                                          */
  /* ------------------------------------------------------------------ */

  const { setPageHeaderSlots } = usePageHeader();
  const { data: partnerDetailForm } = usePartnerDetail(partnerId);
  const partnerName = partnerDetailForm?.data?.[0]?.name;

  useEffect(() => {
    setPageHeaderSlots({ leading: { title: partnerName || 'Partner' } });

    // Clear slots on unmount to avoid leaking header state across pages.
    return () => setPageHeaderSlots({});
  }, [partnerName, setPageHeaderSlots]);

  /* ------------------------------------------------------------------ */
  /* Search state (draft vs applied) + URL sync                          */
  /* ------------------------------------------------------------------ */

  const [appliedSearch, setAppliedSearch] = useState<ProductSearchForm>(() => readSearchFromUrl(searchParams));
  const [draftSearch, setDraftSearch] = useState<ProductSearchForm>(() => ({ ...appliedSearch }));

  const [page, setPage] = useState<number>(() => parsePositiveInt(searchParams.get('page'), 1));

  // Filter panel is collapsed by default.
  const [filtersOpen, setFiltersOpen] = useState(false);

  // Keep state in sync when URL changes (deep link / back-forward / manual URL edit).
  useEffect(() => {
    const nextApplied = readSearchFromUrl(searchParams);
    const nextPage = parsePositiveInt(searchParams.get('page'), 1);

    setAppliedSearch(nextApplied);

    setDraftSearch((prev) => {
      const a = cleanSearchForm(prev);
      const b = cleanSearchForm(nextApplied);

      const same =
        (a.productCode ?? '') === (b.productCode ?? '') &&
        (a.productName ?? '') === (b.productName ?? '') &&
        (a.countryId ?? null) === (b.countryId ?? null) &&
        (a.productSaleType ?? null) === (b.productSaleType ?? null);

      return same ? prev : nextApplied;
    });

    setPage(nextPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const syncUrlFromApplied = useCallback(
    (nextApplied: ProductSearchForm, nextPage: number) => {
      setSearchParams(buildUrlParams(nextApplied, nextPage));
    },
    [setSearchParams],
  );

  const handleSearch = useCallback(() => {
    const nextApplied = cleanSearchForm(draftSearch);
    setAppliedSearch(nextApplied);
    setPage(1);
    syncUrlFromApplied(nextApplied, 1);
  }, [draftSearch, syncUrlFromApplied]);

  const handleReset = useCallback(() => {
    const cleared: ProductSearchForm = {
      productCode: '',
      productName: '',
      countryId: undefined,
      productSaleType: undefined,
    };

    setDraftSearch(cleared);

    const nextApplied = cleanSearchForm(cleared);
    setAppliedSearch(nextApplied);
    setPage(1);
    syncUrlFromApplied(nextApplied, 1);
  }, [syncUrlFromApplied]);

  /**
   * SearchPanel input/chip setter (null = clear).
   * - Text fields use "" so the draft stays controlled.
   * - Select fields use undefined so cleanSearchForm can drop them.
   */
  const setValue: SetSearchValueFn<ProductSearchForm> = useCallback((key, value) => {
    setDraftSearch((prev) => {
      const next = { ...prev } as Record<string, unknown>;

      if (key === 'productCode' || key === 'productName') {
        next[key] = (value ?? '') as string;
      } else {
        next[key] = (value ?? undefined) as unknown;
      }

      return next as ProductSearchForm;
    });
  }, []);

  /* ------------------------------------------------------------------ */
  /* Queries (driven by appliedSearch + page)                            */
  /* ------------------------------------------------------------------ */

  const cleanedApplied = useMemo(() => cleanSearchForm(appliedSearch), [appliedSearch]);

  const { data, isLoading } = usePartnerProductList({ page, searchParam: cleanedApplied });
  const { data: totalCount, isLoading: isLoadingCount } = usePartnerProductCount(cleanedApplied);

  const safeTotalCount = totalCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(safeTotalCount / PAGE_SIZE));
  const rows = data?.data ?? [];

  const isTableLoading = Boolean(isLoading || isLoadingCount);

  // Clamp current page if total count changes (filters applied, etc.).
  useEffect(() => {
    setPage((prev) => clamp(prev, 1, totalPages));
  }, [totalPages]);

  /* ------------------------------------------------------------------ */
  /* Options (country / sale type)                                       */
  /* ------------------------------------------------------------------ */

  const { data: countriesData } = useCountryList();

  const countryOptions: Option<number>[] = useMemo(() => {
    const raw = countriesData?.data ?? [];
    return raw.map((c) => ({ label: c.englishName, value: c.countryId })).sort(sortByLabel);
  }, [countriesData]);

  const saleTypeOptions: Option<ProductSaleType>[] = useMemo(() => {
    const values = Object.values(ProductSaleType) as ProductSaleType[];
    return values.map((t) => ({ value: t, label: PRODUCT_SALE_TYPE_LABELS[t] ?? t })).sort(sortByLabel);
  }, []);

  const selectedCountry = useMemo(() => {
    if (!draftSearch.countryId) return null;
    return countryOptions.find((o) => o.value === draftSearch.countryId) ?? null;
  }, [countryOptions, draftSearch.countryId]);

  const selectedSaleType = useMemo(() => {
    if (!draftSearch.productSaleType) return null;
    return saleTypeOptions.find((o) => o.value === draftSearch.productSaleType) ?? null;
  }, [saleTypeOptions, draftSearch.productSaleType]);

  /* ------------------------------------------------------------------ */
  /* Export (Excel) based on appliedSearch                               */
  /* ------------------------------------------------------------------ */

  const [isExporting, setExporting] = useState(false);

  const handleExport = useCallback(async () => {
    if (isExporting) return;

    try {
      setExporting(true);

      const res = await exportPartnerProductsExcel(cleanedApplied);

      const nowUtc = new Date();
      const kstDate = utcToZonedTime(nowUtc, KOREAN_TIME_ZONE);
      const fileName = `partner-products-${kstDate.toISOString()}.xlsx`;

      downloadBlob(res, fileName);
    } finally {
      setExporting(false);
    }
  }, [cleanedApplied, isExporting]);

  /* ------------------------------------------------------------------ */
  /* Paging (URL is the source of truth for applied state)               */
  /* ------------------------------------------------------------------ */

  const handleChangePage = useCallback(
    (next: number) => {
      const clamped = clamp(next, 1, totalPages);
      setPage(clamped);
      syncUrlFromApplied(appliedSearch, clamped);
    },
    [appliedSearch, syncUrlFromApplied, totalPages],
  );

  /* ------------------------------------------------------------------ */
  /* Navigation helpers (index-only navigation)                          */
  /* ------------------------------------------------------------------ */

  const handleOpenDetail = useCallback(
    (productId: number) => {
      if (!Number.isFinite(productId) || productId <= 0) return;
      navigate(`/my-products/${productId}`);
    },
    [navigate],
  );

  /* ------------------------------------------------------------------ */
  /* Render                                                               */
  /* ------------------------------------------------------------------ */

  // (# + Product Code + Product Name + Order Type)
  const columnsCount = 4;

  return (
    <Box sx={{ width: '100%', minWidth: 0 }}>
      <PartnerProductsSearchPanel
        open={filtersOpen}
        onOpenChange={setFiltersOpen}
        draftSearch={draftSearch}
        setValue={setValue}
        countryOptions={countryOptions}
        saleTypeOptions={saleTypeOptions}
        selectedCountry={selectedCountry}
        selectedSaleType={selectedSaleType}
        onSearch={handleSearch}
        onReset={handleReset}
        onExport={handleExport}
        exportLoading={isExporting}
        autoSearch={PAGE_AUTO_SEARCH}
      />

      <StandardTableToolbar
        page={page}
        pageSize={PAGE_SIZE}
        totalCount={safeTotalCount}
        onChangePage={handleChangePage}
        showTotalCount
        showPageJump
      />

      {(isTableLoading || isExporting) && (
        <Box mt={1}>
          <LinearProgress />
        </Box>
      )}

      <Box mt={1}>
        <StandardTableFrame<any>
          containerSx={{ borderRadius: 2 }}
          minWidth={900}
          columnsCount={columnsCount}
          isLoading={isTableLoading}
          loadingVariant="skeleton"
          skeletonRows={8}
          emptyText="No products found."
          head={
            <TableRow>
              <TableCell width={70} align="right">
                #
              </TableCell>
              <TableCell sx={cellSx.nowrap}>Product Code</TableCell>
              <TableCell>Product Name</TableCell>
              <TableCell align="center" sx={cellSx.nowrap}>
                Order Type
              </TableCell>
            </TableRow>
          }
          rows={rows}
          getRowKey={(row) => String(row.productId)}
          renderRow={(row, index) => {
            const productId = Number(row.productId);
            const canNavigate = Number.isFinite(productId) && productId > 0;
            const saleTypeMeta = getProductSaleTypeMeta(row.productSaleType);

            return (
              <>
                {/* Index-only navigation (no row click). */}
                <TableCell
                  align="right"
                  role={canNavigate ? 'button' : undefined}
                  tabIndex={canNavigate ? 0 : -1}
                  sx={{
                    ...cellSx.number,
                    cursor: canNavigate ? 'pointer' : 'default',
                    textDecoration: canNavigate ? 'underline' : 'none',
                    textUnderlineOffset: canNavigate ? '3px' : undefined,
                    '&:hover': canNavigate ? { opacity: 0.85 } : undefined,
                  }}
                  onClick={() => canNavigate && handleOpenDetail(productId)}
                  onKeyDown={(e) => {
                    if (!canNavigate) return;
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleOpenDetail(productId);
                    }
                  }}
                >
                  {(page - 1) * PAGE_SIZE + index + 1}
                </TableCell>

                {/* Product Code: plain text (link navigation removed by policy). */}
                <TableCell sx={cellSx.nowrap}>{row.productCode || '-'}</TableCell>

                <TableCell sx={{ minWidth: 220 }}>{row.productName || '-'}</TableCell>

                <TableCell align="center" sx={cellSx.nowrap}>
                  <Chip
                    label={saleTypeMeta.label}
                    color={saleTypeMeta.chip.color}
                    size="small"
                    variant={saleTypeMeta.chip.variant}
                  />
                </TableCell>
              </>
            );
          }}
        />
      </Box>

      <Box mt={2} display="flex" justifyContent="center">
        <StandardPagination
          page={page}
          pageSize={PAGE_SIZE}
          totalCount={safeTotalCount}
          onChangePage={handleChangePage}
          size="small"
          shape="rounded"
        />
      </Box>
    </Box>
  );
};

export default PartnerProducts;
